import React, { useEffect, useState } from "react";
import { ScrollText, Loader2, Check, PawPrint } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export function RulesGate() {
  const { user, refreshUser } = useAuth();
  const [rules, setRules] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const show = user && user.onboarded !== false && !user.rules_accepted;

  useEffect(() => {
    if (show && rules === null) {
      api.get("/pack-code").then((r) => setRules(r.data.rules || [])).catch(() => setRules([]));
    }
  }, [show, rules]);

  if (!show) return null;

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/rules/accept");
      await refreshUser();
      toast.success("Welcome to the pack. 🐺");
    } catch {
      toast.error("Couldn't record your acceptance. Try again.");
      setAccepting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="rules-gate">
      <div className="glass border border-amber-500/25 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <ScrollText className="w-6 h-6 text-amber-400" />
          </span>
          <div>
            <h2 className="font-display text-xl text-slate-100">Code of the Pack</h2>
            <p className="text-sm text-slate-400">Every wolf lives by these laws. Read and accept to join the hunt.</p>
          </div>
        </div>

        {rules === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <PawPrint className="w-8 h-8 mx-auto mb-2 text-slate-700" />
            <p className="text-sm">No code has been laid down yet — but by continuing you agree to howl with respect and keep the territory safe.</p>
          </div>
        ) : (
          <div className="space-y-2 my-4" data-testid="rules-gate-list">
            {rules.map((rule, i) => (
              <div key={i} className="flex gap-3 rounded-xl bg-slate-900/50 border border-slate-800 p-3">
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-500/20 to-slate-800 border border-amber-500/30 flex items-center justify-center shrink-0 font-display text-amber-300 text-xs">{i + 1}</span>
                <p className="text-sm text-slate-200 leading-relaxed self-center">{rule}</p>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={accept}
          disabled={accepting}
          data-testid="rules-accept-button"
          className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold py-3 transition-colors"
        >
          {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} I accept the Code of the Pack
        </button>
      </div>
    </div>
  );
}
